import React, { Component, Fragment } from 'react';
import compose from 'lodash/flowRight';
import { connectStateResults } from 'react-instantsearch-core';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import classnames from 'classnames';
import {
    InCategorySearchFilter,
    MasslandingBlock,
    ProductsListing,
} from 'modules';
import { ListingPortal } from 'components';
import {
    searchStatePausable,
    withAlgoliaAppInfo,
    withCategory,
    withFeatureFlags,
    withHeader,
    withInCategorySearch,
    withListingData,
    withListingType,
    withListingTypesConfig,
    withLocation,
    withSmartSticky,
    withUser,
    withSponsoredProducts,
    withLandingProduct,
} from 'connectors';
import Sticky from 'components/Sticky/Sticky'; // keep this import like that, Sticky is not exported in index.js because it use window then break styleguidist
import {
    category as categoryUtils,
    isCategorySearch,
    scroll,
    searchState as searchStateUtil,
} from 'utils';
import {
    algoliaAppInfoShape,
    categoryShape,
    listingTypesShape,
    locationShape,
    userShape,
    landingProductShape,
} from 'shapes';
import Filters from './Filters/Filters';
import { ToggleFiltersPanelButton, CategoryPageSeo } from './components';
import ResultLength from './ResultLength/ResultLength';
import Sorting from './Sorting';
import PromotedFilters from './PromotedFiltersWrapper/PromotedFiltersWrapper';
import PaginationWrapper from './PaginationWrapper/PaginationWrapper';
import { computeRerankedCount, getListingMode, getNbHitsPro } from './utils';
import { SORTING_MODE } from './Sorting/Sorting.util';
import ListingHeader from './ListingHeader/ListingHeader';

import './Listing.scss';
import './Listing.old.scss';

const filtersTheme = {
    items: 'algolia-search__body__filters__toolbar',
};

class Listing extends Component {
    static propTypes = {
        className: string,
        convergence: bool,
        mobileWidth: number.isRequired,
        searchResults: shape({
            hits: arrayOf(object),
            _rawResults: arrayOf(object),
            nbHits: number,
        }),
        searchState: shape({
            menu: shape({
                'categories.last': number,
            }),
        }),
        category: categoryShape,
        listingType: string,
        listingTypes: listingTypesShape.isRequired,
        withTitleSearch: bool.isRequired,
        hasGuidedNavigationEnabled: bool.isRequired,
        hasQuickFiltersEnabled: bool.isRequired,
        hasQuickFiltersCardEnabled: bool.isRequired,
        promotedFilters: bool,
        smartSticky: func.isRequired,
        algoliaAppInfo: algoliaAppInfoShape.isRequired,
        ssrRender: bool,
        hasInCategorySearch: bool.isRequired,
        location: locationShape.isRequired,
        user: userShape,
        landingProduct: landingProductShape.isRequired,
    };

    static defaultProps = {
        className: null,
        convergence: false,
        searchResults: {},
        searchState: {},
        category: null,
        listingType: null,
        promotedFilters: false,
        ssrRender: false,
        user: null,
    };

    constructor(props) {
        super(props);
        this.state = {
            openedFilters: false,
            showConvergenceProducts: false,
            searchState: {},
        };
    }

    static getDerivedStateFromProps(props, state) {
        const searchStateNeedToBeFilled =
            Object.keys(state.searchState).length === 0 &&
            Object.keys(props.searchState).length > 0;

        if (searchStateNeedToBeFilled) {
            return {
                searchState: props.searchState,
            };
        }

        if (
            props.convergence &&
            !state.showConvergenceProducts &&
            searchStateUtil.didUserUpdateSearchState(
                state.searchState,
                props.searchState,
            )
        ) {
            return {
                searchState: props.searchState,
                showConvergenceProducts: true,
            };
        }

        return null;
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {
            category,
            searchResults,
            searchState,
            withTitleSearch,
            className,
        } = this.props;
        const { openedFilters } = this.state;
        // we ignore searching, searchingForFacetValues, isSearchStalled, allSearchResults and error props (provided by connectStateResults)
        return (
            category !== nextProps.category ||
            className !== nextProps.className ||
            withTitleSearch !== nextProps.withTitleSearch ||
            searchResults !== nextProps.searchResults ||
            searchState !== nextProps.searchState ||
            openedFilters !== nextState.openedFilters
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.openedFilters && !this.state.openedFilters) {
            scroll(0, 500, window);
        }
    }

    getSpecificFilters = () => {
        const { category } = this.props;
        return (category && category.context && category.context.filters) || [];
    };

    handleToggleFiltersClick = () => {
        this.setState({
            openedFilters: !this.state.openedFilters,
        });
    };

    canShowProducts = () => {
        const { searchResults, category, ssrRender } = this.props;

        if (ssrRender) {
            return true;
        }

        if (category && !category.context) {
            return false;
        }

        return !!searchResults;
    };

    isInGuidedNavigationExperience = () => {
        const { category } = this.props;
        return (
            category &&
            category.context &&
            category.context.experiences &&
            category.context.experiences.includes(
                categoryUtils.EXPERIENCES.GUIDED_NAVIGATION,
            )
        );
    };

    getCurrentPage = () => {
        const { searchState = {} } = this.props;

        const page =
            searchState.configure &&
            searchState.configure.page &&
            parseInt(searchState.configure.page, 10);

        return Number.isNaN(page) ? 1 : page || 1;
    };

    shouldShowPromotedProduct = () => {
        const { searchState } = this.props;
        return (
            searchStateUtil.hasNoActiveFilter(searchState) &&
            this.getCurrentPage() === 1
        );
    };

    shouldShowPromotedFiltersStep = step => {
        const { user } = this.props;

        if (step.isB2c && !user.is_b2b) return true;
        if (step.isB2b && user.is_b2b) return true;
        return false;
    };

    shouldShowPromotedFilters = () => {
        const { category, promotedFilters, landingProduct } = this.props;

        return !!(
            this.isInGuidedNavigationExperience() &&
            promotedFilters &&
            !landingProduct.modelId &&
            this.getCurrentPage() === 1 &&
            category &&
            category.context &&
            category.context.promotedFilters &&
            category.context.promotedFilters.filter(step =>
                this.shouldShowPromotedFiltersStep(step),
            ).length > 0
        );
    };

    isSearching = () => {
        const { searchResults, searchState, withTitleSearch } = this.props;

        return (
            withTitleSearch &&
            searchState.query &&
            !(
                !searchState ||
                (searchState &&
                    searchState.menu &&
                    searchState.menu['categories.last']) ||
                !searchResults ||
                this.props.category
            )
        );
    };

    showTipsheet = () => {
        const { category } = this.props;

        return (
            category &&
            category.context &&
            category.context.tipsheet &&
            this.getCurrentPage() === 1
        );
    };

    showQuickFilters = () => {
        const {
            hasQuickFiltersEnabled,
            hasQuickFiltersCardEnabled,
            hasGuidedNavigationEnabled,
            category,
        } = this.props;

        return (
            hasGuidedNavigationEnabled &&
            (hasQuickFiltersEnabled || hasQuickFiltersCardEnabled) &&
            (
                (category &&
                    category.context &&
                    category.context.experiences) ||
                []
            ).includes(categoryUtils.EXPERIENCES.GUIDED_NAVIGATION) &&
            this.getCurrentPage() === 1
        );
    };

    render() {
        const {
            className,
            convergence,
            mobileWidth,
            category,
            searchResults,
            listingType,
            smartSticky,
            algoliaAppInfo,
            listingTypes,
            hasInCategorySearch,
            location,
            user,
        } = this.props;

        const { openedFilters } = this.state;

        const rootClassnames = classnames(className, 'listing-root');

        const listingDisplayMode = getListingMode(
            category?.context?.experiences,
            user.isB2B,
        );
        const gridClassnames = classnames(
            'listing-root__grid',
            'products',
            className,
            {
                [`products--${listingDisplayMode}`]: listingDisplayMode,
            },
        );
        const brightnessClassnames = classnames(
            'listing-root__filters-overlay listing-body__mobile-refinement',
            {
                'listing-root__filters-overlay--active': openedFilters,
            },
        );

        let excludedFilters = listingTypes.category.excludedFilters;
        if (convergence) {
            excludedFilters =
                (listingTypes || {})[listingType] &&
                listingTypes[listingType].excludedFilters;
        }

        const isInCategorySearch = !!(
            this.state.searchState.query && listingType === 'category'
        );
        const showPromotedFilters = this.shouldShowPromotedFilters();
        const showProducts = this.canShowProducts();
        const showPromotedProduct =
            !showPromotedFilters && this.shouldShowPromotedProduct();
        const isIntraCategory = isCategorySearch({
            location,
            listingType,
        });
        const rerankedCount = computeRerankedCount(searchResults);
        const categorySeoContent =
            (category &&
                category.id &&
                category.context &&
                category.context.seo_blocks) ||
            {};

        return (
            showProducts && (
                <ListingPortal>
                    <Fragment>
                        <ListingHeader />
                        {hasInCategorySearch &&
                            isIntraCategory &&
                            searchResults?.nbHits > 0 && (
                                <InCategorySearchFilter
                                    listingType={listingType}
                                />
                            )}
                        {/*
                            TMP fix while maintaining the old listing
                            .category-page__wrapper allows to keep the content centered
                        */}
                        <div className="category-page__wrapper">
                            <div className={rootClassnames}>
                                <Sticky
                                    className="sorting-and-filter-buttons listing-body__mobile-refinement"
                                    smartSticky={smartSticky}
                                >
                                    <Sorting
                                        defaultRefinement={
                                            algoliaAppInfo.defaultIndex
                                        }
                                        items={algoliaAppInfo.indexes}
                                        mobileWidth={mobileWidth}
                                        mode={SORTING_MODE.BAR}
                                    />
                                    <ToggleFiltersPanelButton
                                        excludedFilters={excludedFilters}
                                        onClick={this.handleToggleFiltersClick}
                                    />
                                </Sticky>
                                <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                                    className={brightnessClassnames}
                                    onClick={this.handleToggleFiltersClick}
                                />

                                <Filters
                                    className="listing-root__body__filters"
                                    theme={filtersTheme}
                                    hasCategoryDetected={!!category}
                                    specificFilters={this.getSpecificFilters()}
                                    open={openedFilters}
                                    mobileWidth={mobileWidth}
                                    nbResults={
                                        (searchResults &&
                                            searchResults.nbHits) ||
                                        0
                                    }
                                    excludedFilters={excludedFilters}
                                    onClose={this.handleToggleFiltersClick}
                                />
                                <div className="listing-root__body__listing">
                                    <div
                                        className={gridClassnames}
                                        data-reranked-count={rerankedCount}
                                    >
                                        <div className="listing-root__body__listing__promoted-filters">
                                            {showPromotedFilters && (
                                                <PromotedFilters
                                                    className="listing__promoted-filters"
                                                    categoryId={category.id}
                                                />
                                            )}
                                        </div>

                                        <div className="listing-root__result-length-and-sorting">
                                            <ResultLength
                                                nbHitsPro={
                                                    searchResults &&
                                                    getNbHitsPro(
                                                        searchResults.facets,
                                                    )
                                                }
                                                shouldShowProProducts={
                                                    user.isB2B || user.is_b2b
                                                }
                                                nbHits={
                                                    searchResults &&
                                                    searchResults.nbHits
                                                }
                                            />
                                            <Sorting
                                                defaultRefinement={
                                                    algoliaAppInfo.defaultIndex
                                                }
                                                items={algoliaAppInfo.indexes}
                                                groupIndexes
                                                mobileWidth={mobileWidth}
                                                mode={SORTING_MODE.LIST}
                                                className="listing-body__desktop-refinement"
                                            />
                                        </div>

                                        <ProductsListing
                                            showPromotedProduct={
                                                showPromotedProduct
                                            }
                                            showTipsheet={this.showTipsheet()}
                                            showQuickFilters={this.showQuickFilters()}
                                            isInCategorySearch={
                                                isInCategorySearch
                                            }
                                            rerankedCount={rerankedCount}
                                            mode={listingDisplayMode}
                                        />

                                        <PaginationWrapper
                                            nbHits={
                                                searchResults &&
                                                searchResults.nbHits
                                            }
                                        />
                                    </div>
                                </div>
                                {category && category.id && (
                                    <CategoryPageSeo
                                        className="listing-root__category-page-seo"
                                        title={categorySeoContent.title}
                                        description={
                                            categorySeoContent.description
                                        }
                                    />
                                )}
                            </div>
                            <div id="wrapper-product-history" />
                            <MasslandingBlock className="instant-search__masslanding-block" />
                        </div>
                    </Fragment>
                </ListingPortal>
            )
        );
    }
}

export default compose(
    withFeatureFlags({
        hasB2BProductCardRedesign: 'ff.browsing.b2b.listing.card.style',
        hasB2B: 'ff.global.b2b',
        convergence: 'browsing.search.convergence',
        mobileWidth: 'instantSearch.mobileWidth',
        mobileWidthOld: 'instantSearch.old.mobileWidth',
        withTitleSearch: 'instantSearch.titleSearch',
        promotedFilters: 'browsing.guided.navigation.promotedfilters',
        hasGuidedNavigationEnabled: 'browsing.guided.navigation',
        hasQuickFiltersEnabled: 'browsing.guided.navigation.quickfilters',
        hasQuickFiltersCardEnabled:
            'browsing.guided.navigation.quickfilters.card',
        hasInCategorySearch: 'relevance.in.category.search',
    }),
    connectStateResults,
    withSmartSticky,
    withCategory,
    withListingType,
    withAlgoliaAppInfo,
    withListingTypesConfig,
    withLocation,
    withInCategorySearch,
    withHeader,
    withListingData,
    withUser,
    withSponsoredProducts,
    withLandingProduct,
    searchStatePausable({
        keepSearchState: true,
        keepSearchResults: true,
    }),
)(Listing);
